import polyConfig from '../../poly.json';

const DB_NAME = 'linkit_dpop';
const STORE_NAME = 'secure_enclave';
const KEYPAIR_ID = 'dpop_keypair';
const PUBLIC_JWK_ID = 'dpop_public_jwk';
const DPOP_SKEW_MS = 5 * 60 * 1000;

let installed = false;

function base64UrlEncode(bytes) {
  let binary = '';
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

function encodeJson(value) {
  return base64UrlEncode(new TextEncoder().encode(JSON.stringify(value)));
}

async function sha256Base64Url(value) {
  const digest = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(value));
  return base64UrlEncode(new Uint8Array(digest));
}

async function openEnclave() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = (event) => {
      event.target.result.createObjectStore(STORE_NAME);
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function storeGet(store, key) {
  return new Promise((resolve, reject) => {
    const request = store.get(key);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function storePut(store, value, key) {
  return new Promise((resolve, reject) => {
    const request = store.put(value, key);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

export async function getHardwareBoundKey() {
  const db = await openEnclave();
  const readTx = db.transaction(STORE_NAME, 'readonly');
  const existingKeyPair = await storeGet(readTx.objectStore(STORE_NAME), KEYPAIR_ID);
  const existingPublicJwk = await storeGet(readTx.objectStore(STORE_NAME), PUBLIC_JWK_ID);

  if (existingKeyPair?.privateKey && existingKeyPair?.publicKey && existingPublicJwk) {
    return { keyPair: existingKeyPair, publicJwk: existingPublicJwk };
  }

  const keyPair = await window.crypto.subtle.generateKey(
    { name: 'ECDSA', namedCurve: 'P-256' },
    false,
    ['sign', 'verify']
  );
  const publicJwk = await window.crypto.subtle.exportKey('jwk', keyPair.publicKey);

  const writeTx = db.transaction(STORE_NAME, 'readwrite');
  const store = writeTx.objectStore(STORE_NAME);
  await storePut(store, keyPair, KEYPAIR_ID);
  await storePut(store, publicJwk, PUBLIC_JWK_ID);
  return { keyPair, publicJwk };
}

export async function generateDPoPHeader(url, method = 'GET', accessToken = '') {
  if (!window.crypto?.subtle || !window.indexedDB) return '';

  const { keyPair, publicJwk } = await getHardwareBoundKey();
  const requestUrl = new URL(url, window.location.origin);
  const issuedAt = Date.now();
  const payload = {
    v: 1,
    jti: window.crypto.randomUUID(),
    htm: String(method || 'GET').toUpperCase(),
    htu: requestUrl.pathname,
    iat: issuedAt,
    jwk: {
      kty: publicJwk.kty,
      crv: publicJwk.crv,
      x: publicJwk.x,
      y: publicJwk.y,
    },
    ...(accessToken ? { ath: await sha256Base64Url(accessToken) } : {}),
  };
  const encodedPayload = encodeJson(payload);
  const signature = await window.crypto.subtle.sign(
    { name: 'ECDSA', hash: { name: 'SHA-256' } },
    keyPair.privateKey,
    new TextEncoder().encode(encodedPayload)
  );

  return `${encodedPayload}.${base64UrlEncode(new Uint8Array(signature))}`;
}

function getHeader(headers, name) {
  if (!headers) return '';
  if (headers instanceof Headers) return headers.get(name) || '';
  const found = Object.entries(headers).find(([key]) => key.toLowerCase() === name.toLowerCase());
  return found?.[1] ? String(found[1]) : '';
}

function shouldProtectRequest(url) {
  const requestUrl = new URL(url, window.location.origin);
  return requestUrl.origin === window.location.origin && (
    requestUrl.pathname.startsWith('/api/v1/') ||
    requestUrl.pathname === '/api/mail' ||
    requestUrl.pathname === '/api/safety'
  );
}

export function installDpopFetchInterceptor() {
  if (installed || typeof window === 'undefined' || typeof window.fetch !== 'function') return;
  installed = true;
  const originalFetch = window.fetch.bind(window);

  window.fetch = async (input, init = {}) => {
    const request = input instanceof Request ? input : null;
    const url = request?.url || input;
    const options = { ...init };
    const method = (options.method || request?.method || 'GET').toUpperCase();
    const headers = new Headers(request?.headers || {});
    new Headers(options.headers || {}).forEach((value, key) => headers.set(key, value));

    if (shouldProtectRequest(url, options)) {
      headers.set(polyConfig.HEADER_NAME, polyConfig.HEADER_VALUE);
      const authorization = getHeader(headers, 'Authorization');
      const accessToken = authorization.startsWith('Bearer ') ? authorization.slice(7).trim() : '';
      if (accessToken && !accessToken.startsWith('linkit_live_')) {
        const proof = await generateDPoPHeader(url, method, accessToken).catch(() => '');
        if (proof) headers.set('DPoP', proof);
      }
    }

    options.headers = headers;
    return originalFetch(input, options);
  };

  window.setTimeout(() => {
    const now = Date.now();
    if (now - Number(sessionStorage.getItem('linkit_dpop_healthcheck') || 0) > DPOP_SKEW_MS) {
      sessionStorage.setItem('linkit_dpop_healthcheck', String(now));
    }
  }, 0);
}
